import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownloadOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DatePicker, Dropdown, Statistic, Table, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChartLine, FaMoneyBillWave, FaReceipt, FaShoppingCart, FaTags } from 'react-icons/fa';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import getRangeReports, { RangeReport } from '@/api/queries/getRangeReports';
import { exportData, ExportFormat } from '@/helpers/exportData';
import { formatNumber } from '@/helpers/formatNumber';
import useCurrency from '@/hooks/user/useCurrency';

const PercentageChange = ({ value }: { value: number }) => {
  if (!value) return <MinusOutlined className="text-gray-400" />;

  const isPositive = value > 0;
  const color = isPositive ? 'text-green-500' : 'text-red-500';
  const Icon = isPositive ? ArrowUpOutlined : ArrowDownOutlined;

  return (
    <div className={`flex items-center gap-1 ${color} text-xs`}>
      <Icon />
      <span>{Math.abs(value)}%</span>
    </div>
  );
};

const MetricCard = ({
  metric,
  value,
  percentageChange,
  isSelected,
  onClick,
  loading,
}: {
  metric: (typeof metrics)[0];
  value: number;
  percentageChange: number;
  isSelected: boolean;
  onClick: () => void;
  loading: boolean;
}) => (
  <Card
    key={metric.key}
    className="cursor-pointer transition-all duration-300 ease-in-out shadow-sm hover:shadow-lg"
    onClick={onClick}
    style={{
      border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
    }}
  >
    <div className="space-y-2">
      <Statistic
        title={metric.title}
        value={value}
        prefix={metric.icon}
        valueStyle={{ color: metric.color }}
        loading={loading}
      />
      <div className="flex justify-end">
        <PercentageChange value={percentageChange} />
      </div>
    </div>
  </Card>
);

const metrics = [
  {
    key: 'grossSales',
    title: 'Gross Sales',
    icon: <FaMoneyBillWave />,
    color: '#10B981',
  },
  {
    key: 'refunds',
    title: 'Refunds',
    icon: <FaReceipt />,
    color: '#EF4444',
  },
  {
    key: 'discounts',
    title: 'Discounts',
    icon: <FaTags />,
    color: '#F97316',
  },
  {
    key: 'netSales',
    title: 'Net Sales',
    icon: <FaChartLine />,
    color: '#0EA5E9',
  },
  {
    key: 'grossProfit',
    title: 'Gross Profit',
    icon: <FaShoppingCart />,
    color: '#6366F1',
  },
];

const Home = () => {
  const { t } = useTranslation();
  const [selectedMetric, setSelectedMetric] = useState('grossSales');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

  const { currency } = useCurrency();

  const { data: rangeReportData, isPending: isPendingRangeReport } = useQuery({
    queryKey: ['getRangeReports', startDate, endDate],
    queryFn: () => getRangeReports({ startDate, endDate }),
  });

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setStartDate(dates[0].format('YYYY-MM-DD'));
      setEndDate(dates[1].format('YYYY-MM-DD'));
    }
  };

  if (!isPendingRangeReport && !rangeReportData) {
    return <div>Error fetching data</div>;
  }

  const columns = [
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: t('grossSales'),
      dataIndex: 'grossSales',
      key: 'grossSales',
      render: (value: number) => formatNumber(value, currency),
    },
    {
      title: t('refunds'),
      dataIndex: 'refunds',
      key: 'refunds',
      render: (value: number) => formatNumber(value, currency),
    },
    {
      title: t('discounts'),
      dataIndex: 'discounts',
      key: 'discounts',
      render: (value: number) => formatNumber(value, currency),
    },
    {
      title: t('netSales'),
      dataIndex: 'netSales',
      key: 'netSales',
      render: (value: number) => formatNumber(value, currency),
    },
    {
      title: t('grossProfit'),
      dataIndex: 'grossProfit',
      key: 'grossProfit',
      render: (value: number) => formatNumber(value, currency),
    },
    {
      title: t('totalCOGS'),
      dataIndex: 'totalCOGS',
      key: 'totalCOGS',
      render: (value: number) => formatNumber(value, currency),
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4 bg-white p-4 rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h3 className="text-lg font-semibold m-0">{t('dateRange')}</h3>
          <DatePicker.RangePicker
            value={[dayjs(startDate), dayjs(endDate)]}
            onChange={handleDateRangeChange}
            className="w-full md:w-auto"
            allowClear={false}
            format="YYYY-MM-DD"
            ranges={{
              [t('today')]: [dayjs(), dayjs()],
              [t('thisWeek')]: [dayjs().startOf('week'), dayjs()],
              [t('thisMonth')]: [dayjs().startOf('month'), dayjs()],
              [t('lastMonth')]: [
                dayjs().subtract(1, 'month').startOf('month'),
                dayjs().subtract(1, 'month').endOf('month'),
              ],
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {metrics.map((metric) => (
          <MetricCard
            key={metric.key}
            metric={metric}
            value={rangeReportData?.totals?.[metric.key]?.toFixed(2)}
            percentageChange={rangeReportData?.percentageChanges?.[metric.key]?.toFixed(2)}
            isSelected={selectedMetric === metric.key}
            onClick={() => setSelectedMetric(metric.key)}
            loading={isPendingRangeReport}
          />
        ))}
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h3 className="text-lg font-semibold mb-4">
          {metrics.find((m) => m.key === selectedMetric)?.title} {t('trend')}
        </h3>
        <ReportGraph
          selectedMetric={selectedMetric}
          reportData={rangeReportData}
          isPendingReport={isPendingRangeReport}
        />
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold m-0">{t('dailyBreakdown')}</h3>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'csv',
                  label: t('exportCSV'),
                  onClick: () => handleExport('csv', rangeReportData),
                },
                {
                  key: 'json',
                  label: t('exportJSON'),
                  onClick: () => handleExport('json', rangeReportData),
                },
                {
                  key: 'xlsx',
                  label: t('exportExcel'),
                  onClick: () => handleExport('xlsx', rangeReportData),
                },
              ],
            }}
            disabled={!rangeReportData?.days?.length || isPendingRangeReport}
          >
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!rangeReportData?.days?.length || isPendingRangeReport}
            >
              {t('exportData')}
            </Button>
          </Dropdown>
        </div>
        <Table
          dataSource={rangeReportData?.days?.map((day, index) => ({
            ...day,
            key: index,
          }))}
          columns={columns}
          loading={isPendingRangeReport}
          scroll={{ x: 'max-content' }}
          pagination={false}
        />
      </div>
    </div>
  );
};

// Handler function for exporting data
const handleExport = (format: ExportFormat, rangeReportData: RangeReport | undefined) => {
  if (!rangeReportData?.days || rangeReportData.days.length === 0) return;

  // Export the data using our helper function
  exportData(
    rangeReportData.days,
    format,
    `daily-breakdown-${new Date().toISOString().split('T')[0]}`,
    'Daily Breakdown'
  );
};

export default Home;

const formatCurrency = (value: number, currencyCode: string = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    notation: 'compact',
  }).format(value);
};

const ReportGraph = ({
  selectedMetric,
  reportData,
  isPendingReport,
}: {
  selectedMetric: string;
  reportData: RangeReport;
  isPendingReport: boolean;
}) => {
  const metric = metrics.find((m) => m.key === selectedMetric);
  const { currency } = useCurrency();

  if (isPendingReport) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!reportData?.days?.length) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={reportData.days}>
        <defs>
          <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={metric?.color} stopOpacity={0.8} />
            <stop offset="95%" stopColor={metric?.color} stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="date" tickFormatter={(day) => dayjs(day).format('MMM DD')} stroke="#666" />
        <YAxis tickFormatter={(value) => formatCurrency(value, currency)} stroke="#666" />
        <Tooltip
          formatter={(value: any) => [formatCurrency(value, currency), '']}
          labelFormatter={(label) => dayjs(label).format('MMMM DD, YYYY')}
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #f0f0f0',
            borderRadius: '4px',
            padding: '8px',
          }}
        />
        <Legend />
        <Area
          name={metric?.title}
          type="monotone"
          dataKey={selectedMetric}
          stroke={metric?.color}
          fill="url(#colorMetric)"
          strokeWidth={2}
          animationDuration={300}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
